import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Video, AudioLines, FileText, Image } from "lucide-react";
import { toast } from "sonner";

interface TutorialListProps {
  onEdit: (tutorial: any) => void;
}

export const TutorialList = ({ onEdit }: TutorialListProps) => {
  const { data: tutorials, refetch } = useQuery({
    queryKey: ["tutorials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tutorials")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("tutorials")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast.success("Tutoriel supprimé avec succès");
      refetch();
    } catch (error) {
      console.error("Error deleting tutorial:", error);
      toast.error("Erreur lors de la suppression du tutoriel");
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4" />;
      case "audio":
        return <AudioLines className="w-4 h-4" />;
      case "image":
        return <Image className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {tutorials?.map((tutorial) => (
        <Card key={tutorial.id}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getTypeIcon(tutorial.type)}
                <CardTitle className="text-lg">{tutorial.title}</CardTitle>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(tutorial)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(tutorial.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <CardDescription>{tutorial.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {tutorial.content}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};